/**
 * DistribuicaoSection — S1 Bloco A (multi-cota)
 * Lista de distribuição N:N (contratos + clientes), confirmação da lista
 * (Comercial/Coordenação) e confirmação de crédito contrato a contrato (4.b).
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X, Lock, CheckCircle2, Clock3, Share2 } from 'lucide-react';
import clsx from 'clsx';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { parseCreditos, validarTravaDistribuicao, exigeVinculoDistribuicao } from '@/lib/distribuicao';
import type { Demanda, Solicitante } from '@/lib/types';

interface Props {
  demanda: Demanda;
  solicitantes: Solicitante[];
}

export function DistribuicaoSection({ demanda, solicitantes }: Props) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const canManage = user?.papel === 'Comercial' || user?.papel === 'Coordenação';
  const [err, setErr] = useState<string | null>(null);

  const contratosQ = useQuery({
    queryKey: ['contratos'],
    queryFn: () => api.contratos.list().then((r) => r.data),
    staleTime: 60_000,
  });
  const contratos = contratosQ.data ?? [];
  const contratoNome = useMemo(
    () => new Map(contratos.map((c) => [c.id, c.identificador])),
    [contratos],
  );
  const solicitanteNome = useMemo(
    () => new Map(solicitantes.map((s) => [s.id, s.nome])),
    [solicitantes],
  );

  const contratosIds = demanda.contratosIds ?? (demanda.contratoId ? [demanda.contratoId] : []);
  const clientesIds = demanda.clientesIds ?? [];
  const confirmada = demanda.distribuicaoConfirmada ?? false;
  const creditos = parseCreditos(demanda.distribuicaoCreditos);
  const trava = validarTravaDistribuicao(demanda);
  const exige = exigeVinculoDistribuicao(demanda);

  const salvar = useMutation({
    mutationFn: (body: { contratosIds?: string[]; clientesIds?: string[]; confirmada?: boolean }) =>
      api.demandas.atualizarDistribuicao(demanda.id, body).then((r) => r.data),
    onSuccess: () => { setErr(null); qc.invalidateQueries({ queryKey: ['demandas'] }); },
    onError: (e) => setErr(e instanceof ApiError ? ((e.body as { message?: string })?.message ?? e.message) : String(e)),
  });

  const confirmarCredito = useMutation({
    mutationFn: (ids: string[]) =>
      api.demandas.confirmarCreditos(demanda.id, ids).then((r) => r.data),
    onSuccess: () => { setErr(null); qc.invalidateQueries({ queryKey: ['demandas'] }); },
    onError: (e) => setErr(e instanceof ApiError ? ((e.body as { message?: string })?.message ?? e.message) : String(e)),
  });

  const creditoPorContrato = new Map(creditos.map((c) => [c.contratoId, c]));

  return (
    <div className="border border-border rounded-lg p-3 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <Share2 className="w-4 h-4 text-muted" />
          Distribuição (multi-cota)
          {confirmada ? (
            <span className="text-[10px] px-1.5 py-0.5 rounded border bg-green/15 text-green border-green/30">confirmada</span>
          ) : exige ? (
            <span className="text-[10px] px-1.5 py-0.5 rounded border bg-yellow/15 text-yellow border-yellow/30">pendente</span>
          ) : null}
        </div>
        {canManage && (
          <button
            type="button"
            className={clsx(
              'inline-flex items-center gap-1.5 text-xs font-semibold rounded border cursor-pointer transition-colors shadow-sm active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none',
              confirmada
                ? 'px-2.5 py-1.5 border-border text-muted hover:text-red hover:border-red/40'
                : 'px-3 py-1.5 bg-green text-white border-green hover:bg-green/90',
            )}
            disabled={salvar.isPending || contratosIds.length === 0}
            onClick={() => salvar.mutate({ confirmada: !confirmada })}
            title={contratosIds.length === 0 ? 'Vincule ao menos um contrato' : 'Clique para confirmar a distribuição'}
          >
            {confirmada ? (
              <>
                <X className="w-3.5 h-3.5" />
                Desfazer confirmação
              </>
            ) : (
              <>
                <CheckCircle2 className="w-3.5 h-3.5" />
                Confirmar distribuição
              </>
            )}
          </button>
        )}
      </div>

      {trava && demanda.status !== 'Publicada' && (
        <div className="flex items-start gap-2 text-xs text-yellow bg-yellow/10 border border-yellow/30 rounded p-2">
          <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>Conclusão travada — {trava}</span>
        </div>
      )}

      {/* Contratos */}
      <div>
        <div className="text-[11px] uppercase tracking-wide text-muted mb-1">Contratos ({contratosIds.length})</div>
        <div className="flex flex-wrap gap-1.5">
          {contratosIds.map((id) => {
            const credito = creditoPorContrato.get(id);
            return (
              <span key={id} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-border bg-surface">
                {contratoNome.get(id) ?? id}
                {credito && (credito.status === 'confirmado' ? (
                  <span title={`Crédito confirmado por ${credito.confirmadoPor ?? '—'}`}>
                    <CheckCircle2 className="w-3.5 h-3.5 text-green" />
                  </span>
                ) : (
                  <span title="Aguardando confirmação de crédito">
                    <Clock3 className="w-3.5 h-3.5 text-yellow" />
                  </span>
                ))}
                {credito?.status === 'aguardando' && canManage && demanda.status === 'Publicada' && (
                  <button
                    type="button"
                    className="text-[10px] text-green underline"
                    disabled={confirmarCredito.isPending}
                    onClick={() => confirmarCredito.mutate([id])}
                  >
                    creditar
                  </button>
                )}
                {canManage && (
                  <button
                    type="button"
                    onClick={() => salvar.mutate({ contratosIds: contratosIds.filter((x) => x !== id) })}
                    className="text-muted hover:text-red"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </span>
            );
          })}
          {contratosIds.length === 0 && <span className="text-xs text-muted">Nenhum contrato vinculado.</span>}
        </div>
        {canManage && (
          <select
            className="input mt-1.5 w-full text-xs"
            value=""
            onChange={(e) => {
              if (e.target.value && !contratosIds.includes(e.target.value)) {
                // auto-preenche o cliente do contrato na lista de distribuição
                const ct = contratos.find((c) => c.id === e.target.value);
                const cliente = ct?.solicitanteId;
                salvar.mutate({
                  contratosIds: [...contratosIds, e.target.value],
                  ...(cliente && !clientesIds.includes(cliente)
                    ? { clientesIds: [...clientesIds, cliente] }
                    : {}),
                });
              }
            }}
          >
            <option value="">+ adicionar contrato…</option>
            {contratos
              .filter((c) => !contratosIds.includes(c.id))
              .sort((a, b) => (a.solicitanteNome ?? a.identificador).localeCompare(b.solicitanteNome ?? b.identificador, 'pt-BR'))
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.solicitanteNome ? `${c.solicitanteNome} — ${c.identificador}` : c.identificador}
                </option>
              ))}
          </select>
        )}
      </div>

      {/* Clientes distribuição */}
      <div>
        <div className="text-[11px] uppercase tracking-wide text-muted mb-1">Clientes que recebem ({clientesIds.length})</div>
        <div className="flex flex-wrap gap-1.5">
          {clientesIds.map((id) => (
            <span key={id} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-border bg-surface">
              {solicitanteNome.get(id) ?? id}
              {canManage && (
                <button
                  type="button"
                  onClick={() => salvar.mutate({ clientesIds: clientesIds.filter((x) => x !== id) })}
                  className="text-muted hover:text-red"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </span>
          ))}
          {clientesIds.length === 0 && <span className="text-xs text-muted">Portal exibirá apenas para o solicitante original.</span>}
        </div>
        {canManage && (
          <select
            className="input mt-1.5 w-full text-xs"
            value=""
            onChange={(e) => {
              if (e.target.value && !clientesIds.includes(e.target.value)) {
                salvar.mutate({ clientesIds: [...clientesIds, e.target.value] });
              }
            }}
          >
            <option value="">+ adicionar cliente…</option>
            {solicitantes
              .filter((s) => s.status === 'Ativo' && !clientesIds.includes(s.id))
              .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))
              .map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
          </select>
        )}
      </div>

      {/* Confirmação de crédito em lote */}
      {canManage && demanda.status === 'Publicada' && creditos.some((c) => c.status === 'aguardando') && (
        <button
          type="button"
          className="btn-secondary text-xs w-full"
          disabled={confirmarCredito.isPending}
          onClick={() => confirmarCredito.mutate(creditos.filter((c) => c.status === 'aguardando').map((c) => c.contratoId))}
        >
          Confirmar crédito de todos os aguardando ({creditos.filter((c) => c.status === 'aguardando').length})
        </button>
      )}

      {err && <p className="text-xs text-red">{err}</p>}
    </div>
  );
}
